import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { safeRedirect } from '../utils/safeRedirect';
import { CheckCircle, XCircle, Loader2, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '../config/api';

const API_URL = API_BASE;

export default function SSOCallbackPage() {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Completing authentication...');
  const [errorDetails, setErrorDetails] = useState('');
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  
  useEffect(() => {
    const processCallback = async () => {
      // Microsoft 365 SSO (platform config): backend completes the OIDC exchange
      // and redirects here with the session token in the URL fragment.
      const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
      const hashToken = hash.get('token');
      if (hashToken) {
        try {
          setMessage('Setting up your session...');
          // Remove the token from the address bar immediately
          window.history.replaceState(null, '', window.location.pathname);
          const me = await axios.get(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${hashToken}` } });
          localStorage.setItem('token', hashToken);
          localStorage.setItem('user', JSON.stringify(me.data));
          const isNew = hash.get('new_user') === '1';
          setStatus('success');
          setMessage(isNew ? 'Welcome! Your account has been created.' : 'Welcome back!');
          const target = hash.get('redirect_to') || '/dashboard';
          const safeTarget = safeRedirect(target);
          // Hard redirect so AuthContext loads the new session
          setTimeout(() => { window.location.href = safeTarget; }, 800);
        } catch (err) {
          setStatus('error');
          setMessage('Authentication failed');
          setErrorDetails('Your Microsoft 365 session could not be verified. Please sign in again.');
        }
        return;
      }

      // Get OAuth parameters from URL
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      
      // Handle OAuth errors
      if (error) {
        setStatus('error');
        setMessage('Authentication failed');
        setErrorDetails(errorDescription || error);
        return;
      }
      
      // Validate required parameters
      if (!code || !state) {
        setStatus('error');
        setMessage('Invalid callback parameters');
        setErrorDetails('Missing authorization code or state token');
        return;
      }
      
      try {
        setMessage('Verifying your identity...');
        
        // Exchange code for tokens via backend
        const response = await axios.post(`${API_URL}/api/v1/auth/sso/callback`, {
          code,
          state,
          session_state: searchParams.get('session_state')
        });
        
        const { access_token, user, is_new_user } = response.data;
        
        setMessage('Setting up your session...');
        
        // Store auth data
        if (setAuthData) {
          setAuthData({
            token: access_token,
            user: user
          });
        } else {
          // Fallback: store directly in localStorage
          localStorage.setItem('token', access_token);
          localStorage.setItem('user', JSON.stringify(user));
        }
        
        setStatus('success');
        setMessage(is_new_user ? 'Welcome! Your account has been created.' : 'Welcome back!');
        
        // Show toast
        toast.success(is_new_user ? 'Welcome to Cerebro!' : 'Welcome back!', {
          description: `Signed in as ${user.email}`
        });
        
        // Get return path and navigate
        const returnPath = sessionStorage.getItem('sso_return_path') || '/dashboard';
        sessionStorage.removeItem('sso_return_path');
        
        // Short delay to show success state
        setTimeout(() => {
          navigate(returnPath, { replace: true });
        }, 1500);
        
      } catch (err) {
        console.error('SSO callback error:', err);
        setStatus('error');
        setMessage('Authentication failed');
        setErrorDetails(err.response?.data?.detail || 'Failed to complete authentication. Please try again.');
      }
    };
    
    processCallback();
  }, [searchParams, navigate, setAuthData]);

  return (
    <div className="min-h-screen bg-navigate-dark flex items-center justify-center px-4" data-testid="sso-callback-page">
      <div className="w-full max-w-md text-center">
        {/* Status Icon */}
        <div className="mb-6">
          {status === 'processing' && (
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600/20 rounded-full">
              <Loader2 className="h-10 w-10 text-blue-400 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-600/20 rounded-full">
              <CheckCircle className="h-10 w-10 text-green-400" />
            </div>
          )}
          {status === 'error' && (
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-600/20 rounded-full">
              <XCircle className="h-10 w-10 text-red-400" />
            </div>
          )}
        </div>
        
        {/* Status Message */}
        <h1 className="text-2xl font-bold text-white mb-2">{message}</h1>
        
        {/* Error Details */}
        {status === 'error' && errorDetails && (
          <p className="text-sm text-red-400 mb-6">{errorDetails}</p>
        )}
        
        {/* Processing Message */}
        {status === 'processing' && (
          <p className="text-zinc-400 mb-6">
            Please wait while we complete your sign-in with Microsoft Entra ID...
          </p>
        )}
        
        {/* Success Message */}
        {status === 'success' && (
          <p className="text-zinc-400 mb-6">
            Redirecting to your dashboard...
          </p>
        )}
        
        {/* Error Actions */}
        {status === 'error' && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
            >
              Standard Login
            </button>
          </div>
        )}
        
        {/* Security Badge */}
        <div className="mt-8 inline-flex items-center gap-2 text-xs text-zinc-500">
          <Shield className="h-4 w-4" />
          <span>Secured by Microsoft Entra ID</span>
        </div>
      </div>
    </div>
  );
}
